import { CountryCode } from '@/__generated__/storefront/graphql';
import { getApolloClients } from '@/shared/utils/apollo-client';
import { shopifyGidToId } from '@/shared/utils/shopify/id-formatter';

import {
  ProductData,
  ProductNode,
  ProductResult,
  THUMBNAIL_HEIGHT,
  THUMBNAIL_WIDTH,
  WishlistItemLike
} from './wishlist.types';
import { searchProductsForWishlistQuery } from './wishlistStorefront.queries';

/**
 * Result type for product data fetching operations
 */
export type ProductDataResult = {
  imageByItemId: Record<string, string>;
  productDataByItemId: Record<string, ProductData>;
};

/**
 * Convert a URL to a thumbnail URL with specified dimensions
 * @param url - Original image URL
 * @returns Thumbnail URL with width, height and crop parameters
 */
export function toThumbnailUrl(url: string): string {
  if (!url) return url;
  const hasQuery = url.includes('?');
  const sep = hasQuery ? '&' : '?';
  // Center-crop thumbnails for wishlist
  return `${url}${sep}width=${THUMBNAIL_WIDTH}&height=${THUMBNAIL_HEIGHT}&crop=center`;
}

/**
 * Preload images for immediate visibility
 * @param urls - Array of image URLs to preload
 * @param maxConcurrent - Maximum number of images to preload concurrently
 */
export function preloadImages(urls: string[], maxConcurrent = 3): void {
  urls.slice(0, maxConcurrent).forEach((url) => {
    const img = new Image();
    img.src = url;
  });
}

/**
 * Internal maps used during product processing
 */
type ProductMaps = {
  skuToImage: Map<string, string>;
  productGroupIdToImage: Map<string, string>;
  skuToProductData: Map<string, ProductData>;
  productGroupIdToProductData: Map<string, ProductData>;
};

/**
 * Process a single product node and populate the maps
 * This is the unified processProduct function that was duplicated in the original code
 *
 * @param product - The product node to process
 * @param isPattern - Whether this is a pattern (digital product)
 * @param allSkus - Array of all SKUs to match against
 * @param productGroupIds - Array of product group IDs to match against
 * @param maps - Object containing the maps to populate
 */
function processProductNode(
  product: ProductNode,
  isPattern: boolean,
  allSkus: string[],
  productGroupIds: string[],
  maps: ProductMaps
): void {
  if (product?.__typename !== 'Product') return;

  const featuredUrl: string | undefined =
    product?.featuredImage?.url || undefined;
  const productGroupId = product?.productGroupIdMetafield?.value;
  const contentMetafield = product.metafields?.find(
    (m) => m?.key === 'yarn_composition'
  );
  const content = contentMetafield?.value || '';

  // Check against ALL SKUs
  const variantNodes = product?.variants?.nodes || [];

  // Store product data for product groups (matched by product_group_id metafield)
  if (productGroupId && productGroupIds.includes(productGroupId)) {
    if (featuredUrl) {
      maps.productGroupIdToImage.set(productGroupId, featuredUrl);
    }

    const productPrice = product.priceRange?.minVariantPrice || null;
    const productCompareAtPrice =
      product.compareAtPriceRange?.minVariantPrice || null;

    // Check if product-level compareAtPrice actually indicates a discount
    const hasProductLevelDiscount =
      productPrice &&
      productCompareAtPrice &&
      parseFloat(productCompareAtPrice.amount) >
        parseFloat(productPrice.amount);

    // Fall back to first variant's compareAtPrice if product-level compareAtPrice doesn't indicate a discount
    // Only use variant compareAtPrice if it's actually greater than the price (indicates a discount)
    let finalCompareAtPrice = hasProductLevelDiscount
      ? productCompareAtPrice
      : null;

    if (!finalCompareAtPrice) {
      const variantWithDiscount = variantNodes.find((v) => {
        if (!v?.compareAtPrice || !v?.price) return false;
        return parseFloat(v.compareAtPrice.amount) > parseFloat(v.price.amount);
      });
      finalCompareAtPrice = variantWithDiscount?.compareAtPrice || null;
    }

    const productData: ProductData = {
      title: product.title || '',
      content: content,
      size: null, // Product groups don't have a specific variant size
      price: productPrice,
      compareAtPrice: finalCompareAtPrice,
      handle: product.handle || null,
      variantId: null, // Product group doesn't have a specific variant
      availableForSale: product.availableForSale ?? null,
      quantityAvailable: null, // Product groups don't have specific quantity
      isPattern,
      isGiftCard: product.isGiftCard ?? false
    };
    maps.productGroupIdToProductData.set(productGroupId, productData);
  }

  // Process variants to match by SKU
  variantNodes.forEach((v) => {
    const sku: string | undefined = v?.sku || undefined;
    const vImg: string | undefined = v?.image?.url || undefined;
    const variantId: string | undefined = v?.id || undefined;
    const variantTitle: string | undefined = v?.title || undefined;

    if (sku && allSkus.includes(sku)) {
      // Prioritize variant image over product featured image
      maps.skuToImage.set(sku, vImg || featuredUrl || '');

      // Store product data for variants
      const variantProductData: ProductData = {
        title: product.title || '',
        content: content,
        size: variantTitle || null,
        price: v.price || product.priceRange?.minVariantPrice || null,
        compareAtPrice:
          v.compareAtPrice ||
          product.compareAtPriceRange?.minVariantPrice ||
          null,
        handle: product.handle || null,
        variantId: variantId ? shopifyGidToId(variantId) : null,
        availableForSale:
          v.availableForSale ?? product.availableForSale ?? null,
        quantityAvailable: v.quantityAvailable ?? null,
        isPattern,
        isGiftCard: product.isGiftCard ?? false
      };
      maps.skuToProductData.set(sku, variantProductData);
    }
  });
}

/**
 * Build a Shopify search query string from an array of SKUs
 * @param skus - Array of SKUs to search for
 * @returns Search query string or 'false' if no SKUs
 */
function buildSkuSearchQuery(skus: string[]): string {
  return skus.length > 0
    ? skus.map((sku) => `variants.sku:"${sku}"`).join(' OR ')
    : 'false';
}

/**
 * Execute all Storefront API queries for product data
 * @param productGroupIds - Array of product group IDs
 * @param allSkus - Array of all SKUs
 * @param storefront - Apollo client for Storefront API
 * @returns Combined query results
 */
async function executeStorefrontQueries(
  productGroupIds: string[],
  allSkus: string[],
  storefront: Awaited<ReturnType<typeof getApolloClients>>['storefront']
): Promise<{
  combinedProductNodes: unknown[];
  patternNodes: unknown[];
}> {
  // No filter - allow products with any price including 0 (point-based patterns)
  // and without requiring entity_type metafield
  const permissiveProductFilters: Array<Record<string, unknown>> = [];

  // Build individual product queries for each product group ID
  const productQueries = productGroupIds.map((productGroupId) => {
    const productFilters = [
      ...permissiveProductFilters,
      {
        productMetafield: {
          namespace: 'hobbii',
          key: 'product_group_id',
          value: productGroupId
        }
      }
    ];
    return storefront.query({
      query: searchProductsForWishlistQuery,
      variables: {
        productQuery: '',
        productFilters,
        patternQuery: 'false', // Only query products in this batch
        country: (window?.Shopify?.country as CountryCode) || CountryCode.Us,
        firstProducts: 50, // Get multiple products per group if needed
        firstPatterns: 0
      },
      fetchPolicy: 'cache-first'
    });
  });

  // Build pattern and SKU queries
  const patternQuery = buildSkuSearchQuery(allSkus);
  const skuProductQuery = buildSkuSearchQuery(allSkus);

  // Execute all product group queries in parallel
  const productResults =
    productGroupIds.length > 0 ? await Promise.all(productQueries) : [];

  // Execute pattern query separately if we have SKUs
  let patternResult: { data?: unknown } | null = null;
  if (allSkus.length > 0) {
    patternResult = await storefront.query({
      query: searchProductsForWishlistQuery,
      variables: {
        productQuery: 'false',
        productFilters: permissiveProductFilters,
        patternQuery,
        country: (window?.Shopify?.country as CountryCode) || CountryCode.Us,
        firstProducts: 0,
        firstPatterns: allSkus.length
      },
      fetchPolicy: 'cache-first'
    });
  }

  // Execute SKU product query (products by SKU - no filter to catch all products)
  // This finds paid products that have SKU but might not have entity_type metafield
  let skuProductResult: { data?: unknown } | null = null;
  if (allSkus.length > 0) {
    skuProductResult = await storefront.query({
      query: searchProductsForWishlistQuery,
      variables: {
        productQuery: skuProductQuery,
        productFilters: [], // No filters - find ALL products matching SKU
        patternQuery: 'false',
        country: (window?.Shopify?.country as CountryCode) || CountryCode.Us,
        firstProducts: Math.max(allSkus.length * 2, 50), // Request more results
        firstPatterns: 0
      },
      fetchPolicy: 'cache-first'
    });
  }

  // Combine all product results into a single array
  const combinedProductNodes: unknown[] = [];
  for (let i = 0; i < productResults.length; i++) {
    const result = productResults[i];
    const typedResult = result as unknown as ProductResult;
    const nodes = typedResult.data?.products?.nodes ?? [];
    combinedProductNodes.push(...nodes);
  }

  // Add SKU product results
  if (skuProductResult) {
    const typedResult = skuProductResult as unknown as ProductResult;
    const nodes = typedResult.data?.products?.nodes ?? [];
    combinedProductNodes.push(...nodes);
  }

  // Get pattern nodes
  const patternNodes: unknown[] = patternResult
    ? ((patternResult.data as { patterns?: { nodes?: unknown[] } | null })
        ?.patterns?.nodes ?? [])
    : [];

  return { combinedProductNodes, patternNodes };
}

/**
 * Process query results and build product data maps
 * @param combinedProductNodes - Array of product nodes from queries
 * @param patternNodes - Array of pattern nodes from queries
 * @param allSkus - Array of all SKUs to match
 * @param productGroupIds - Array of product group IDs to match
 * @returns ProductMaps with populated data
 */
function processQueryResults(
  combinedProductNodes: unknown[],
  patternNodes: unknown[],
  allSkus: string[],
  productGroupIds: string[]
): ProductMaps {
  const maps: ProductMaps = {
    skuToImage: new Map<string, string>(),
    productGroupIdToImage: new Map<string, string>(),
    skuToProductData: new Map<string, ProductData>(),
    productGroupIdToProductData: new Map<string, ProductData>()
  };

  // Track which SKUs and product group IDs are patterns
  const patternSkus = new Set<string>();
  const patternProductGroupIds = new Set<string>();

  // Process patterns FIRST (downloadable patterns)
  // Patterns search specifically filters for entity_type = "pattern"
  patternNodes.forEach((node) => {
    const patternNode = node as ProductNode;
    processProductNode(patternNode, true, allSkus, productGroupIds, maps);
    // Track pattern identifiers to avoid overwriting with products
    const pgid = patternNode?.productGroupIdMetafield?.value;
    if (pgid) patternProductGroupIds.add(pgid);
    patternNode?.variants?.nodes?.forEach((v) => {
      if (v?.sku) patternSkus.add(v.sku);
    });
  });

  // Process products SECOND (physical products)
  // Skip items that were already processed as patterns
  combinedProductNodes.forEach((node) => {
    const productNode = node as ProductNode;
    const pgid = productNode?.productGroupIdMetafield?.value;
    // Skip if this product was already processed as a pattern
    const isAlreadyPattern =
      (pgid && patternProductGroupIds.has(pgid)) ||
      productNode?.variants?.nodes?.some(
        (v) => v?.sku && patternSkus.has(v.sku)
      );
    if (!isAlreadyPattern) {
      processProductNode(productNode, false, allSkus, productGroupIds, maps);
    }
  });

  return maps;
}

/**
 * Build image and product data maps for wishlist items from processed product maps
 * @param items - Array of wishlist items
 * @param maps - Processed product maps
 * @returns ProductDataResult with image and product data maps
 */
function buildItemMaps(
  items: WishlistItemLike[],
  maps: ProductMaps
): ProductDataResult {
  const imageByItemId: Record<string, string> = {};
  const productDataByItemId: Record<string, ProductData> = {};

  for (const item of items) {
    let url = '';
    let productData: ProductData | undefined;

    if (item.sku && maps.skuToImage.has(item.sku)) {
      url = maps.skuToImage.get(item.sku) || '';
      productData = maps.skuToProductData.get(item.sku);
    } else if (
      item.product_group_identifier &&
      maps.productGroupIdToImage.has(item.product_group_identifier)
    ) {
      url = maps.productGroupIdToImage.get(item.product_group_identifier) || '';
      productData = maps.productGroupIdToProductData.get(
        item.product_group_identifier
      );
    }

    if (url) {
      imageByItemId[item.id] = toThumbnailUrl(url);
    }

    if (productData) {
      productDataByItemId[item.id] = productData;
    }
  }

  return { imageByItemId, productDataByItemId };
}

/**
 * Fetch product data and images for wishlist items
 * This is the unified function that replaces both fetchWishlistImages and fetchProductDataForItems
 *
 * @param items - Array of wishlist items to fetch product data for
 * @returns ProductDataResult with image and product data maps
 */
export async function fetchProductDataForItems(
  items: WishlistItemLike[]
): Promise<ProductDataResult> {
  if (!items.length) {
    return { imageByItemId: {}, productDataByItemId: {} };
  }

  const { storefront } = await getApolloClients();

  // Extract unique product group IDs
  const productGroupIds = Array.from(
    new Set(
      items
        .map((i) => i.product_group_identifier || undefined)
        .filter((v): v is string => Boolean(v))
    )
  );

  // Extract unique SKUs
  const allSkus = Array.from(
    new Set(
      items
        .map((i) => i.sku || undefined)
        .filter((v): v is string => Boolean(v))
    )
  );

  // Execute all Storefront API queries
  const { combinedProductNodes, patternNodes } = await executeStorefrontQueries(
    productGroupIds,
    allSkus,
    storefront
  );

  // Process query results into maps
  const maps = processQueryResults(
    combinedProductNodes,
    patternNodes,
    allSkus,
    productGroupIds
  );

  // Build final item maps
  return buildItemMaps(items, maps);
}

/**
 * Check if items need product data fetching (for optimization)
 * @param items - Array of wishlist items
 * @param existingImageMap - Existing image map from store
 * @returns true if fetching is needed
 */
export function shouldFetchProductData(
  items: WishlistItemLike[],
  existingImageMap: Record<string, string>
): boolean {
  if (!items.length) return false;

  // Check if all items already have images cached
  const missingImages = items.filter((item) => !existingImageMap[item.id]);

  // If all items have images, skip fetching (but still allow incremental updates)
  return !(
    missingImages.length === 0 && Object.keys(existingImageMap).length > 0
  );
}
